import { forwardRef, useId, useState, type ChangeEvent, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/utils/cn';
import { FormField } from '../FormField/FormField';
import { Typography } from '../Typography';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Controls the CSS `resize` behaviour of the textarea. */
export type TextareaResize = 'none' | 'vertical' | 'both';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Field label — rendered as a `<label>` above the textarea via the Primer Typography component. */
  label?: string;
  /**
   * Error message. When provided:
   * - Activates error state styling (border, background)
   * - Replaces `helperText` in the text slot below the textarea
   * - Text is rendered in `text-error` colour
   */
  error?: string;
  /**
   * Helper text shown below the textarea when there is no `error`.
   * Rendered in `text-bodylight` colour via the Primer Typography component.
   */
  helperText?: string;
  /**
   * Controls whether and how the user can resize the textarea.
   * - `"none"` — resize handle is hidden; field height is fixed (default)
   * - `"vertical"` — user can resize vertically only
   * - `"both"` — user can resize in both directions
   * @default "none"
   */
  resize?: TextareaResize;
  /**
   * When `true`, renders a character count in the bottom-right corner of the
   * field. The count reflects the current number of characters typed.
   *
   * Colour follows field state: `text-bodylight` by default, `text-error` when
   * the field is in error state.
   *
   * @default false
   */
  showCount?: boolean;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

/**
 * Styles applied to the visual textarea wrapper (`<div>`).
 *
 * The wrapper — not the `<textarea>` itself — carries border, background,
 * radius, and padding so the field appearance is consistent with Input.
 *
 * When resize is enabled the resize handle is applied to the wrapper, not the
 * native `<textarea>`. The textarea is always `resize-none` and fills the
 * wrapper via `w-full`. This prevents the textarea from being dragged to a
 * size smaller than the styled border box.
 */
const wrapperBase =
  'flex flex-col px-4 py-4 rounded-small border bg-surface transition-colors duration-150 overflow-hidden';

const wrapperDefault = 'border-default';

const wrapperError = 'border-error bg-error';

/** Maps the `resize` prop to the Tailwind resize utility applied to the **wrapper**. */
const wrapperResizeClass: Record<TextareaResize, string> = {
  none: 'resize-none',
  vertical: 'resize-y',
  both: 'resize',
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Primer Textarea component.
 *
 * A multi-line form text field that matches the layout and state model of the
 * Primer `Input` component. Handles label, helper text, and error state as
 * first-class concerns, all wired up with the correct Primer design tokens.
 *
 * ### States
 * - **Default** — `border-default`, `bg-surface`, `text-body`
 * - **Error** — `border-error`, `bg-error`; helper text slot shows `error` in `text-error`
 *
 * ### Resize
 * Controlled via the `resize` prop (`"none"` | `"vertical"` | `"both"`).
 * Defaults to `"none"` so the field height is fully controlled by `rows`.
 *
 * ### Character count
 * Set `showCount` to render a live character count in the bottom-right corner
 * of the field. The count is tracked internally — no `value`/`onChange` wiring
 * is required by the consumer. Colour follows field state: `text-bodylight`
 * normally, `text-error` when an error is active.
 *
 * ### Layout stability
 * The sub-text row (helper / error text) is always present in the DOM so the
 * field never changes height when transitioning into or out of error state.
 *
 * ### Accessibility
 * The label is always associated with the textarea via a matching `htmlFor`/`id`
 * pair. When no `id` is provided by the consumer, a stable id is generated
 * automatically via React's `useId` hook so the association is always correct.
 *
 * ### Typography
 * - Label — `Typography variant="formLabel" size="default"` (renders a `<label>`)
 * - Helper / error text — `Typography variant="body" size="small"`
 * - Textarea value / placeholder — `body-small` class applied directly to the
 *   native `<textarea>` element
 *
 * @example
 * // Basic
 * <Textarea label="Bio" placeholder="Tell us about yourself…" />
 *
 * @example
 * // Error state
 * <Textarea label="Bio" error="Bio is required" />
 *
 * @example
 * // With helper text and vertical resize
 * <Textarea label="Notes" helperText="Max 500 characters" resize="vertical" rows={6} />
 *
 * @example
 * // With character count
 * <Textarea label="Bio" showCount />
 *
 * @example
 * // Character count + maxLength enforced natively
 * <Textarea label="Bio" showCount maxLength={500} />
 *
 * @see src/primer/README.md
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    label,
    error,
    helperText,
    resize = 'none',
    id,
    rows = 4,
    className = '',
    showCount = false,
    onChange,
    defaultValue,
    value,
    ...props
  },
  ref
) {
  // Stable generated id — used when the consumer does not supply one so the
  // label <htmlFor> always has a matching textarea id.
  const generatedId = useId();
  const textareaId = id ?? generatedId;

  const hasError = Boolean(error);

  // ── Character count ────────────────────────────────────────────────────────
  // Track the current character count internally so showCount works regardless
  // of whether the consumer controls the field or leaves it uncontrolled.
  // Seed from value/defaultValue so the count is correct on first render.
  const initialLength =
    typeof value === 'string'
      ? value.length
      : typeof defaultValue === 'string'
        ? defaultValue.length
        : 0;

  const [charCount, setCharCount] = useState(initialLength);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setCharCount(e.target.value.length);
    onChange?.(e);
  };

  // Colour for the count — mirrors field state (error → text-error, else bodylight)
  const countColor = hasError ? 'error' : 'bodylight';

  return (
    <FormField label={label} error={error} helperText={helperText} inputId={textareaId}>
      {/* ── Textarea wrapper ──────────────────────────────────────────────
       * The visual border/background lives on this wrapper div, matching the
       * same pattern as Input for consistent field appearance.
       * flex-col so the textarea and the optional count row stack vertically.
       */}
      <div
        data-textarea-wrapper=""
        className={cn(
          wrapperBase,
          // Resize is applied to the wrapper, not the native <textarea>.
          // The textarea is always resize-none and fills the wrapper via w-full.
          // This prevents the user from dragging the textarea smaller than the
          // styled border box.
          wrapperResizeClass[resize],
          hasError ? wrapperError : wrapperDefault,
          className
        )}
      >
        {/* ── Native textarea ─────────────────────────────────────────── */}
        <textarea
          ref={ref}
          id={textareaId}
          rows={rows}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          className={cn(
            // Typography — body-small
            'body-small',
            // Layout — fill wrapper width; grow vertically to push count row to
            // the bottom when the wrapper is taller than the intrinsic row height.
            'w-full flex-1 resize-none',
            // Transparent background so the wrapper bg token shows through
            'bg-transparent',
            // Text colours — error state switches value text to text-error
            hasError ? 'text-error placeholder:text-error' : 'text-body placeholder:text-bodylight',
            // Remove native focus ring — the wrapper handles focus visually
            'outline-none',
            // Disabled
            'disabled:cursor-not-allowed disabled:text-disabled'
          )}
          {...props}
        />

        {/* ── Character count (optional) ───────────────────────────────── */}
        {showCount && (
          <div className="flex justify-end" aria-live="polite" data-char-count="">
            <Typography variant="body" size="small" color={countColor}>
              {charCount}
            </Typography>
          </div>
        )}
      </div>
    </FormField>
  );
});

export default Textarea;
